// ============================================================
// VCS.ChatPhoto — Kamera-Foto + Sprachbild (Voicephoto)
// - Kamera oeffnen, Foto aufnehmen (Front/Back)
// - Optionale Sprachaufnahme zum Foto (Voicephoto)
// - Preview, Crop, Caption eingeben
// - Upload als normales Foto oder Voicephoto
// ============================================================
window.VCS = window.VCS || {};

VCS.ChatPhoto = class {

    // ── Konstruktor ──────────────────────────────────────────

    constructor() {
        this._view = {};
    }

    // ── Öffentliche Methoden ─────────────────────────────────

    async capturePhoto() {
        if (VCS.state.chatActiveChatId && VCS.state.chatE2EEnabled[VCS.state.chatActiveChatId]) {
            VCS.ui?.showToast?.(t('chatPhotoE2EBlocked'), 'error', 5000);
            return;
        }

        let camStream = null;
        try {
            camStream = await navigator.mediaDevices.getUserMedia({
                video: {facingMode: 'user', width: {ideal: 480}, height: {ideal: 480}},
                audio: false
            });
        } catch (_) {
            try {
                camStream = await navigator.mediaDevices.getUserMedia({video: true, audio: false});
            } catch (_2) {
                VCS.ui?.showToast?.(t('cameraAccessDenied') || 'Kein Zugriff auf die Kamera', 'error');
                return;
            }
        }

        const overlay = document.createElement('div');
        overlay.className = 'chat-photo-capture-overlay';
        const _t = (key, fb) => { const v = TRANSLATIONS[VCS.state.currentLang]?.[key] || TRANSLATIONS.de[key]; return v || fb; };
        overlay.innerHTML = `
        <div class="chat-photo-capture-container">
            <div class="chat-photo-capture-video-wrap">
                <video class="chat-photo-capture-video" autoplay playsinline muted></video>
                <div class="chat-photo-capture-hint">${escapeHtml(_t('chatPhotoHintSnap', 'Tippe auf den Auslöser um ein Foto aufzunehmen'))}</div>
            </div>
            <div class="chat-photo-capture-preview hidden">
                <img class="chat-photo-capture-img" alt="">
                <div class="chat-photo-capture-transcript"></div>
                <div class="chat-photo-capture-rec-indicator hidden">
                    <span class="chat-recording-dot"></span>
                    <span class="chat-photo-capture-rec-time">0:00</span>
                </div>
            </div>
            <div class="chat-photo-capture-progress hidden">
                <div class="chat-photo-capture-spinner"></div>
                <div class="chat-photo-capture-progress-text">${escapeHtml(_t('chatPhotoSending', 'Wird gesendet…'))}</div>
            </div>
            <div class="chat-photo-capture-actions">
                <button type="button" class="chat-photo-capture-btn chat-photo-capture-btn--cancel">${escapeHtml(_t('cancel', 'Abbrechen'))}</button>
                <button type="button" class="chat-photo-capture-btn chat-photo-capture-btn--snap">
                    <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="11"/>
                        <circle cx="12" cy="12" r="8" fill="currentColor"/>
                    </svg>
                </button>
                <button type="button" class="chat-photo-capture-btn chat-photo-capture-btn--flip">
                    <svg class="chat-flip-icon" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path class="chat-flip-circle" d="M21 12a9 9 0 1 1-3.6-7.2" />
                        <path d="M21 3v6h-6" />
                    </svg>
                </button>
                <button type="button" class="chat-photo-capture-btn chat-photo-capture-btn--record hidden">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                        <path d="M12 1a4 4 0 0 0-4 4v6a4 4 0 0 0 8 0V5a4 4 0 0 0-4-4z"/>
                        <path d="M19 10a1 1 0 0 0-2 0 5 5 0 0 1-10 0 1 1 0 0 0-2 0 7 7 0 0 0 6 6.93V20a1 1 0 0 0 2 0v-3.07A7 7 0 0 0 19 10z"/>
                    </svg>
                    ${escapeHtml(_t('chatPhotoSpeak', 'Sprechen'))}
                </button>
                <button type="button" class="chat-photo-capture-btn chat-photo-capture-btn--stop hidden">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                        <rect x="4" y="4" width="16" height="16" rx="2"/>
                    </svg>
                    ${escapeHtml(_t('stop', 'Stopp'))}
                </button>
                <button type="button" class="chat-photo-capture-btn chat-photo-capture-btn--send hidden">${escapeHtml(_t('tooltipSend', 'Senden'))}</button>
            </div>
        </div>`;
        document.body.appendChild(overlay);

        const camVideo = overlay.querySelector('.chat-photo-capture-video');
        const previewDiv = overlay.querySelector('.chat-photo-capture-preview');
        const previewImg = overlay.querySelector('.chat-photo-capture-img');
        const transcriptEl = overlay.querySelector('.chat-photo-capture-transcript');
        const recIndicator = overlay.querySelector('.chat-photo-capture-rec-indicator');
        const recTimeEl = overlay.querySelector('.chat-photo-capture-rec-time');
        const snapBtn = overlay.querySelector('.chat-photo-capture-btn--snap');
        const recordBtn = overlay.querySelector('.chat-photo-capture-btn--record');
        const stopBtn = overlay.querySelector('.chat-photo-capture-btn--stop');
        const sendBtn = overlay.querySelector('.chat-photo-capture-btn--send');
        const cancelBtn = overlay.querySelector('.chat-photo-capture-btn--cancel');

        let currentFacing = 'user';
        camVideo.srcObject = camStream;
        camVideo.play().catch(() => {});

        const flipBtn = overlay.querySelector('.chat-photo-capture-btn--flip');
        flipBtn.addEventListener('click', async () => {
            const newFacing = currentFacing === 'user' ? 'environment' : 'user';
            flipBtn.classList.remove('spinning');
            void flipBtn.offsetWidth;
            flipBtn.classList.add('spinning');
            setTimeout(() => flipBtn.classList.remove('spinning'), 550);
            camStream.getTracks().forEach(tr => tr.stop());
            try {
                camStream = await navigator.mediaDevices.getUserMedia({
                    video: {facingMode: {exact: newFacing}, width: {ideal: 480}, height: {ideal: 480}},
                    audio: false
                });
            } catch (_) {
                try {
                    camStream = await navigator.mediaDevices.getUserMedia({
                        video: {facingMode: newFacing, width: {ideal: 480}, height: {ideal: 480}},
                        audio: false
                    });
                } catch (_2) {
                    camStream = await navigator.mediaDevices.getUserMedia({
                        video: {facingMode: currentFacing, width: {ideal: 480}, height: {ideal: 480}},
                        audio: false
                    });
                }
            }
            camVideo.srcObject = camStream;
            camVideo.play().catch(() => {});
            currentFacing = newFacing;
        });

        let photoDataUrl = null;
        let audioBlob = null;
        let transcriptText = '';
        let audioRecorder = null;
        let audioStream = null;
        let recognition = null;
        let recTimerInterval = null;
        let recStartTime = 0;
        let recDurationMs = 0;
        let recStatusInterval = null;

        const hintEl = overlay.querySelector('.chat-photo-capture-hint');
        const progressEl = overlay.querySelector('.chat-photo-capture-progress');

        const cleanup = () => {
            if (recStatusInterval) { clearInterval(recStatusInterval); recStatusInterval = null; }
            camStream.getTracks().forEach(tr => tr.stop());
            if (audioStream) audioStream.getTracks().forEach(tr => tr.stop());
            if (recognition) { try { recognition.stop(); } catch(_){} }
            if (recTimerInterval) clearInterval(recTimerInterval);
            overlay.remove();
        };

        cancelBtn.addEventListener('click', cleanup);

        snapBtn.addEventListener('click', () => {
            const vw = camVideo.videoWidth;
            const vh = camVideo.videoHeight;
            const c = document.createElement('canvas');
            c.width = vw;
            c.height = vh;
            const ctx = c.getContext('2d');
            ctx.drawImage(camVideo, 0, 0, vw, vh);
            photoDataUrl = c.toDataURL('image/jpeg', 0.9);
            previewImg.src = photoDataUrl;
            camStream.getTracks().forEach(tr => tr.stop());
            overlay.querySelector('.chat-photo-capture-video-wrap').classList.add('hidden');
            previewDiv.classList.remove('hidden');
            snapBtn.classList.add('hidden');
            flipBtn.classList.add('hidden');
            recordBtn.classList.remove('hidden');
            hintEl.textContent = _t('chatPhotoHintSpeak', 'Tippe auf Sprechen und sage deinen Text');
            hintEl.classList.add('chat-photo-capture-hint--below');
        });

        recordBtn.addEventListener('click', async () => {
            try {
                audioStream = await navigator.mediaDevices.getUserMedia({
                    audio: {echoCancellation: true, noiseSuppression: true}
                });
            } catch (_) {
                VCS.ui?.showToast?.(t('cameraAccessDenied') || 'Kein Zugriff auf das Mikrofon', 'error');
                return;
            }

            const mimeType = MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4'
                : MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : '';
            audioRecorder = new MediaRecorder(audioStream, mimeType ? {mimeType} : {});
            const audioChunks = [];
            audioRecorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunks.push(e.data); };
            audioRecorder.onstop = () => {
                audioBlob = new Blob(audioChunks, {type: mimeType || 'audio/mp4'});
            };
            audioRecorder.start();

            if (VCS.state.chatActiveChatId) {
                const recPayload = { type: 'chat-recording', chatId: VCS.state.chatActiveChatId, phone: VCS.state.chatMyPhone, mode: 'photo' };
                VCS.chat?.wsSend?.(recPayload);
                recStatusInterval = setInterval(() => {
                    if (VCS.state.chatActiveChatId) VCS.chat?.wsSend?.(recPayload);
                }, 3000);
            }

            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            if (SpeechRecognition) {
                recognition = new SpeechRecognition();
                recognition.continuous = true;
                recognition.interimResults = true;
                recognition.lang = VCS.state.currentLang || 'de';
                let finalizedCount = 0;
                let finalText = '';
                recognition.onresult = (e) => {
                    let interim = '';
                    for (let i = 0; i < e.results.length; i++) {
                        if (e.results[i].isFinal) {
                            if (i >= finalizedCount) {
                                finalText += e.results[i][0].transcript + ' ';
                                finalizedCount = i + 1;
                            }
                        } else {
                            interim += e.results[i][0].transcript;
                        }
                    }
                    transcriptText = (finalText + interim).trim();
                    transcriptEl.textContent = transcriptText || '...';
                };
                recognition.start();
            }

            recStartTime = Date.now();
            recIndicator.classList.remove('hidden');
            recTimerInterval = setInterval(() => {
                const elapsed = Math.floor((Date.now() - recStartTime) / 1000);
                const m = Math.floor(elapsed / 60);
                const s = elapsed % 60;
                recTimeEl.textContent = m + ':' + String(s).padStart(2, '0');
            }, 500);

            transcriptEl.textContent = '...';
            recordBtn.classList.add('hidden');
            stopBtn.classList.remove('hidden');
            hintEl.textContent = _t('chatPhotoHintRecording', 'Sprich jetzt… Tippe auf Stopp wenn du fertig bist');
        });

        stopBtn.addEventListener('click', () => {
            if (recStatusInterval) { clearInterval(recStatusInterval); recStatusInterval = null; }
            recDurationMs = recStartTime ? Date.now() - recStartTime : 0;
            if (recognition) { try { recognition.stop(); } catch(_){} }
            if (audioRecorder && audioRecorder.state !== 'inactive') audioRecorder.stop();
            if (audioStream) audioStream.getTracks().forEach(tr => tr.stop());
            if (recTimerInterval) { clearInterval(recTimerInterval); recTimerInterval = null; }
            recIndicator.classList.add('hidden');
            stopBtn.classList.add('hidden');
            sendBtn.classList.remove('hidden');
            hintEl.textContent = _t('chatPhotoHintSend', 'Tippe auf Senden um die Nachricht abzuschicken');
        });

        sendBtn.addEventListener('click', async () => {
            if (!photoDataUrl) return;
            overlay.querySelector('.chat-photo-capture-actions').classList.add('hidden');
            previewDiv.classList.add('hidden');
            hintEl.classList.add('hidden');
            progressEl.classList.remove('hidden');
            try {
                const photoResp = await fetch(photoDataUrl);
                const photoBlob = await photoResp.blob();

                const durationMs = recDurationMs || 0;
                const caption = transcriptText;
                const formData = new FormData();
                formData.append('photo', new File([photoBlob], 'photo.jpg', {type: 'image/jpeg'}));
                if (audioBlob) {
                    const audioExt = audioBlob.type.includes('mp4') ? '.m4a' : '.webm';
                    formData.append('audio', new File([audioBlob], 'voice' + audioExt, {type: audioBlob.type}));
                }
                formData.append('chatId', VCS.state.chatActiveChatId);
                formData.append('senderLang', VCS.state.currentLang || 'de');
                formData.append('caption', caption);
                formData.append('durationMs', String(durationMs));
                formData.append('translate', String(!VCS.state.chatE2EEnabled[VCS.state.chatActiveChatId]));
                if (VCS.state.chatReplyToId) formData.append('replyToId', VCS.state.chatReplyToId);

                cleanup();

                const res = await fetch(VCS.Chat.API_BASE + '/messages/voicephoto', {
                    method : 'POST',
                    headers: VCS.chat?.authHeaders(),
                    body   : formData,
                });
                if (!res.ok) throw new Error('Upload fehlgeschlagen');
                VCS.state.chatReplyToId = null;
            } catch (err) {
                console.error('[chat] voicephoto error:', err);
                VCS.ui?.showToast?.(_t('chatPhotoError', 'Fehler beim Erstellen'), 'error');
            }
        });
    }

    // ── Init ─────────────────────────────────────────────────

    init() {
        // Foto-Button wird bei Bedarf im Record-Popup gebunden
    }
};
